import { z } from 'zod';
import { Id, InterventionStatus, Timestamp } from './common.js';
import { ProfilePhotoOutput } from './dossiers.js';

/**
 * « Base garage » (décision du porteur, 02/10/2026) : annuaire global du garage actif, une seule
 * recherche sur les clients et les véhicules, présents ou non à l'atelier (route `directory.search`).
 *
 * - Recherche insensible à la casse et aux accents (`app_fold` en SQL, `foldText` en JS), plaque comparée
 *   sans tirets ni espaces, téléphone comparé chiffres seuls (« 06 12… » retrouve « +33 6 12… »).
 * - Sans `q` : liste alphabétique paginée (avec `kind=customers`, c'est la liste des clients).
 * - Filtrée par le garage de la session (filtre explicite + RLS) ; fiches effacées exclues ; aucun montant.
 */
export const DirectoryKind = z.enum(['all', 'customers', 'vehicles']);
export type DirectoryKind = z.infer<typeof DirectoryKind>;

/** Longueur maximale de la recherche (au-delà : 400). */
export const DIRECTORY_QUERY_MAX = 100;
/** Taille de page : 25 par défaut, 50 au plus. */
export const DIRECTORY_PAGE_DEFAULT = 25;
export const DIRECTORY_PAGE_MAX = 50;
/** Décalage maximal (pagination simple par décalage ; affiner la recherche au-delà). */
export const DIRECTORY_OFFSET_MAX = 5000;

export const DirectoryQuery = z.strictObject({
  /** Texte libre ; vide ou blanc = pas de recherche (liste alphabétique). */
  q: z.string().trim().max(DIRECTORY_QUERY_MAX).optional(),
  kind: DirectoryKind.default('all'),
  limit: z.coerce.number().int().min(1).max(DIRECTORY_PAGE_MAX).default(DIRECTORY_PAGE_DEFAULT),
  offset: z.coerce.number().int().min(0).max(DIRECTORY_OFFSET_MAX).default(0),
});
export type DirectoryQuery = z.infer<typeof DirectoryQuery>;

/**
 * Champ qui a trouvé le résultat (le premier dans cet ordre de priorité), `null` sans recherche.
 * - `name` : nom du client (« Prénom Nom », « Nom Prénom » ou raison sociale) ;
 * - `phone`, `email` : coordonnées du client ;
 * - `plate`, `vin`, `make`, `model` : véhicule (`model` couvre aussi « marque modèle version ») ;
 * - `owner` : nom du propriétaire d'un véhicule ;
 * - `interventionRequest` : objet d'une intervention (demande du client), restituées et planifiées comprises ;
 * - `workItem` : libellé d'un travail réalisé (« vidange », « plaquettes »…), jamais son prix ;
 * - `customerNotes`, `vehicleNotes` : remarque (interne) de la fiche client ou véhicule. Un client est aussi
 *   trouvé par la remarque d'un de ses véhicules (`vehicleNotes`), un véhicule par celle de son propriétaire
 *   (`customerNotes`). Le texte de la remarque n'est pas renvoyé par l'annuaire (fiche : `customers.get`,
 *   `vehicles.get`).
 */
export const DirectoryMatchField = z.enum([
  'name',
  'phone',
  'email',
  'plate',
  'vin',
  'make',
  'model',
  'owner',
  'interventionRequest',
  'workItem',
  'customerNotes',
  'vehicleNotes',
]);
export type DirectoryMatchField = z.infer<typeof DirectoryMatchField>;

/** Libellés français de `matchedOn` (« Trouvé par : … »). */
export const DIRECTORY_MATCH_LABELS: Readonly<Record<DirectoryMatchField, string>> = {
  name: 'Nom',
  phone: 'Téléphone',
  email: 'E-mail',
  plate: 'Plaque',
  vin: 'VIN',
  make: 'Marque',
  model: 'Modèle',
  owner: 'Propriétaire',
  interventionRequest: 'Objet d’intervention',
  workItem: 'Travaux réalisés',
  customerNotes: 'Remarque de la fiche client',
  vehicleNotes: 'Remarque de la fiche véhicule',
};

/** Dernière intervention (la plus récente entrée), sans aucun montant. `subject` : objet tronqué à 140 caractères. */
export const DirectoryLastIntervention = z.object({
  id: Id,
  receivedAt: Timestamp,
  status: InterventionStatus,
  subject: z.string(),
});
export type DirectoryLastIntervention = z.infer<typeof DirectoryLastIntervention>;

export const DirectoryCustomerResult = z.object({
  type: z.literal('customer'),
  id: Id,
  /** Nom d'affichage du client. */
  label: z.string(),
  /** Coordonnées résumées (« téléphone · e-mail »), ou `null` sans coordonnée. */
  sublabel: z.string().nullable(),
  /** Mêmes champs, mêmes droits que `customers.list` (`intervention.view`). */
  phone: z.string().nullable(),
  email: z.string().nullable(),
  photo: ProfilePhotoOutput.nullable(),
  vehicleCount: z.int().nonnegative(),
  /** Le client a au moins un dossier non clôturé (véhicule présent à l'atelier). */
  atWorkshop: z.boolean(),
  lastIntervention: DirectoryLastIntervention.nullable(),
  matchedOn: DirectoryMatchField.nullable(),
});
export type DirectoryCustomerResult = z.infer<typeof DirectoryCustomerResult>;

export const DirectoryVehicleResult = z.object({
  type: z.literal('vehicle'),
  id: Id,
  /** Plaque (forme normalisée). */
  label: z.string(),
  /** « Marque Modèle Version · Propriétaire ». */
  sublabel: z.string(),
  plate: z.string(),
  make: z.string(),
  model: z.string(),
  version: z.string().nullable(),
  photo: ProfilePhotoOutput.nullable(),
  owner: z.object({ id: Id, name: z.string() }),
  /** Le véhicule a un dossier non clôturé. */
  atWorkshop: z.boolean(),
  /** Dossier non clôturé du véhicule (lien direct), sinon `null`. */
  currentInterventionId: Id.nullable(),
  lastIntervention: DirectoryLastIntervention.nullable(),
  /**
   * 02/10/2026 : dernière action (ISO UTC, même définition que `InterventionSummary.lastActivityAt`) sur le
   * dossier en cours du véhicule, sinon sur sa dernière intervention ; `null` sans intervention.
   */
  lastActivityAt: Timestamp.nullable(),
  matchedOn: DirectoryMatchField.nullable(),
});
export type DirectoryVehicleResult = z.infer<typeof DirectoryVehicleResult>;

export const DirectoryResult = z.discriminatedUnion('type', [DirectoryCustomerResult, DirectoryVehicleResult]);
export type DirectoryResult = z.infer<typeof DirectoryResult>;

/**
 * Page de résultats. Ordre : avec `q`, par force de la correspondance (nom ou plaque, puis coordonnées et
 * VIN, puis véhicule et propriétaire, puis interventions), puis alphabétique ; sans `q`, alphabétique
 * (nom du client, plaque du véhicule). `total` : nombre de résultats pour la requête (toutes pages) ;
 * `nextOffset` : décalage de la page suivante, `null` en fin de liste.
 */
export const DirectoryPage = z.object({
  items: z.array(DirectoryResult),
  total: z.int().nonnegative(),
  nextOffset: z.int().nonnegative().nullable(),
});
export type DirectoryPage = z.infer<typeof DirectoryPage>;
